/**
 * wizardEdit: the wizard opened as an edit gets the WHOLE record.
 *
 * Card 1217916966128774. The two edit doors (the collection tile's Edit and
 * List it, and Quick Edit's More Details) open the wizard on a draft seeded
 * from the page's row, which knows the title, the value, the toggles and the
 * subcategory and nothing else: no packaging, no weight, no grade, no cert,
 * no year, no size. The wizard then showed the new-listing defaults for all of
 * those, and a save (now that saves are real, see wizardPublish.ts saveEdit)
 * would have written them over the seller's own values.
 *
 * So, right after a door's own handler has run (it sets editMoreReturn and the
 * draft; we listen in CAPTURE and defer past it), this reads the record from
 * the server, the same call the app's own edit makes, and hands it to the
 * bridge, which writes every field into the draft, marks the edit SEEDED and
 * repaints. wizardPublish refuses to save an edit that is not seeded, so a
 * failed read cannot become a lossy save: the seller sees the message, goes
 * back and tries again.
 *
 * Round two (the adversarial pass on the first build): every door numbers its
 * edit, and a record that comes back for a number that is no longer the
 * current edit's is dropped, so a slow read for one item never lands in the
 * next item's draft. The bridge fills only the fields the seller has not
 * touched since the door. The read also carries each stored photo's stored
 * thumbnail, so a save sends thumbnails back rather than the originals.
 *
 * A listed item: GET /api/listings/:id (the public row, every column the
 * update route accepts). A collection-only item: GET /api/collections, the
 * owner's closet, and the row by id (the public single-item read hides pieces
 * taken down by moderation; the owner's own list does not).
 *
 * A save pressed while the read is still out, or after it failed, is refused
 * by wizardPublish (editSeeded false) and starts the read again from here, so
 * "not loaded yet, try again" is true.
 */
import { mapListingRow, splitTradeFor, type ApiCollectionItem, type ApiListingRow } from "./floorTransport";

export const EDIT_LOAD_FAILED_MESSAGE = "Couldn't load this item's details. Go back and try again.";
/** A listing in a trade (status pending) is locked by the server; said at the door, not after the whole wizard. */
export const EDIT_IN_TRADE_MESSAGE = "This listing is in a trade and can't be edited right now.";
/** Sold, traded or removed: closed for good. */
export const EDIT_CLOSED_MESSAGE = "This listing is closed and can't be edited.";
const LOCKED_IN_TRADE = new Set(["pending"]);
const LOCKED_CLOSED = new Set(["sold", "traded", "removed"]);

/** The record as the bridge's __gbzSeedWizardEdit takes it: the wizard's own
 *  names and shapes (a value string, a grader of "Raw" for an ungraded item,
 *  pounds and ounces apart). A key that is absent leaves the door's seed. */
export interface WizardEditSeed {
  title?: string;
  description?: string;
  category?: string | null;
  subcategory?: string | null;
  itemSize?: string | null;
  brand?: string;
  condition?: string | null;
  value?: number | null;
  acceptsCash?: boolean;
  acceptsItems?: boolean;
  acceptsHybrid?: boolean;
  images?: string[];
  year?: number | null;
  grader?: string | null;
  grade?: string | null;
  cert?: string;
  weightLb?: number | null;
  weightOz?: number | null;
  dimL?: string;
  dimW?: string;
  dimH?: string;
  /** Each stored photo's stored thumbnail, by the photo's url. */
  thumbs?: Record<string, string>;
  /** The item's own Trade for, the stored comma list split (PR B), present only when the wire
   *  carried the column. Seeded into the item's own chips, all picked, joined to anything the
   *  seller added while the read was out. */
  tradeFor?: string[];
  /** The listing's status on the wire (active, unlisted, paused, under_review,
   *  pending, sold, traded, removed). Availability is read from THIS, never
   *  from what the page happened to know. */
  status?: string | null;
}

type EditContext = { id: string | null; cid: string | null; kind: string | null; seeded: boolean; seq: number | null };

interface EditWindow extends Window {
  __gbzEditContext?: () => EditContext | null;
  __gbzSeedWizardEdit?: (rec: WizardEditSeed, seq?: number | null) => boolean;
  __gbzCancelEdit?: () => boolean;
  toast?: (message: string) => void;
}

export const DOOR_SELECTOR = '[data-mgract^="edit:"], [data-mgract^="listit:"], #editl-more';
/** wizardPublish's button: a press on an unseeded edit re-reads the record. */
const PUBLISH_SELECTOR = "#wz-publish";

/** `graded` is a tri-state on the wire; the wizard's grader chip carries it. */
export function graderFor(graded: boolean | null | undefined, grader: string | null | undefined): string | null {
  if (graded === false) return "Raw";
  if (graded === true && grader) return grader;
  return null;
}

/** One weight on the wire, two inputs in the wizard. */
export function splitWeight(lb: number | string | null | undefined): { weightLb: number | null; weightOz: number | null } {
  const n = Number(lb);
  if (!Number.isFinite(n) || n <= 0) return { weightLb: null, weightOz: null };
  const whole = Math.floor(n);
  const oz = Math.round((n - whole) * 16);
  return oz === 16 ? { weightLb: whole + 1, weightOz: 0 } : { weightLb: whole, weightOz: oz };
}

const dim = (v: number | string | null | undefined): string => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
};

type PublicListing = ApiListingRow["listing"] & {
  itemSize?: string | null;
  noBrand?: boolean | null;
  acquiredYear?: number | null;
  certNumber?: string | null;
  packageWeightLb?: number | string | null;
  packageLengthIn?: number | string | null;
  packageWidthIn?: number | string | null;
  packageHeightIn?: number | string | null;
  renditionImages?: string[] | null;
  status?: string | null;
  exchangeFor?: string | null;
};

export function seedFromListing(row: ApiListingRow): WizardEditSeed {
  const l = row.listing as PublicListing;
  const w = splitWeight(l.packageWeightLb);
  const stored = Array.isArray(l.images) ? l.images : [];
  const renditions = Array.isArray(l.renditionImages) ? l.renditionImages : [];
  const thumbs: Record<string, string> = {};
  stored.forEach((u, i) => {
    const t = renditions[i];
    if (typeof u === "string" && u.length > 0 && typeof t === "string" && t.length > 0) thumbs[u] = t;
  });
  return {
    title: l.title,
    description: l.description ?? "",
    category: l.category,
    subcategory: l.subcategory || null,
    itemSize: l.itemSize || null,
    brand: l.brand ?? "",
    condition: l.condition || null,
    value: l.declaredValue,
    acceptsCash: !!l.acceptsCash,
    acceptsItems: !!l.acceptsItems,
    acceptsHybrid: !!l.acceptsHybrid,
    images: stored.filter((u) => typeof u === "string" && u.length > 0),
    thumbs,
    status: l.status ?? null,
    year: l.acquiredYear ?? null,
    grader: graderFor(l.graded, l.grader),
    grade: l.grade ?? null,
    cert: l.certNumber ?? "",
    weightLb: w.weightLb,
    weightOz: w.weightOz,
    dimL: dim(l.packageLengthIn),
    dimW: dim(l.packageWidthIn),
    dimH: dim(l.packageHeightIn),
    // Only when the wire carried the column: an absent key must not read as "none" (see the
    // bridge's hadTradeFor), so a read that drops the field cannot make a save wipe it.
    ...("exchangeFor" in l ? { tradeFor: splitTradeFor(l.exchangeFor) } : {}),
  };
}

type ClosetRow = ApiCollectionItem & {
  images?: string[] | null;
  category?: string | null;
  subcategory?: string | null;
  itemSize?: string | null;
  condition?: string | null;
  acquiredYear?: number | null;
  graded?: boolean | null;
  grade?: string | null;
  grader?: string | null;
  certNumber?: string | null;
  exchangeFor?: string | null;
};

export function seedFromCollectionItem(r: ApiCollectionItem): WizardEditSeed {
  const c = r as ClosetRow;
  const n = Number(c.estimatedValue);
  const images = (Array.isArray(c.images) ? c.images : [c.imageUrl]).filter((u): u is string => typeof u === "string" && u.length > 0);
  return {
    title: c.title,
    description: c.description ?? "",
    category: c.category ?? null,
    subcategory: c.subcategory || null,
    itemSize: c.itemSize || null,
    condition: c.condition || null,
    value: Number.isFinite(n) ? n : null,
    acceptsCash: !!c.acceptsCash,
    acceptsItems: !!c.acceptsItems,
    acceptsHybrid: !!c.acceptsHybrid,
    images,
    year: c.acquiredYear ?? null,
    grader: graderFor(c.graded, c.grader),
    grade: c.grade ?? null,
    cert: c.certNumber ?? "",
    ...("exchangeFor" in c ? { tradeFor: splitTradeFor(c.exchangeFor) } : {}),
  };
}

export function installWizardEdit(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as EditWindow).toast?.(message);
    },
    defer = (fn: () => void) => { win.setTimeout(fn, 0); },
  }: { notify?: (m: string) => void; defer?: (fn: () => void) => void } = {},
): () => void {
  const target = win as EditWindow;

  async function load(ctx: EditContext): Promise<WizardEditSeed | null> {
    if (ctx.kind === "listing" && ctx.id) {
      const res = await authFetch(`/api/listings/${encodeURIComponent(ctx.id)}`);
      if (!res.ok) return null;
      const row = (await res.json()) as ApiListingRow;
      if (!row || typeof row !== "object" || !row.listing) return null;
      // mapListingRow is the page's own reading of a row (it accepts any status;
      // a locked one is answered from the seed's status below). It THROWS on a
      // row the page cannot read, into the catch below: not one to edit either.
      mapListingRow(row);
      return seedFromListing(row);
    }
    if (ctx.kind === "collection" && ctx.cid) {
      const res = await authFetch("/api/collections");
      if (!res.ok) return null;
      const rows = (await res.json()) as ApiCollectionItem[];
      const mine = Array.isArray(rows) ? rows.find((r) => r && typeof r === "object" && r.id === ctx.cid) : undefined;
      return mine ? seedFromCollectionItem(mine) : null;
    }
    return null;
  }

  /* The edit number whose read is out, so a save pressed twice while it is
     still loading does not start a second read. */
  let inFlight: number | null = null;

  const onClick = (event: Event): void => {
    const el = (event.target as Element | null)?.closest?.(`${DOOR_SELECTOR}, ${PUBLISH_SELECTOR}`);
    if (!el) return;
    /* After the door's own handler: it is what sets editMoreReturn and the
       draft, and it runs in the bubble phase after this capture listener. On
       the publish button, this is the retry for an edit still unseeded. */
    defer(() => {
      const ctx = target.__gbzEditContext?.();
      if (!ctx || ctx.seeded) return;
      if (inFlight !== null && inFlight === ctx.seq) return;
      inFlight = ctx.seq;
      void load(ctx)
        .then((rec) => {
          /* The seller may have left the wizard while the read was out, or
             opened ANOTHER item: a record for an edit that is not the current
             one is dropped, and the bridge checks the number again itself. */
          const now = target.__gbzEditContext?.();
          if (!now || now.seeded || now.seq !== ctx.seq) return;
          if (!rec) { notify(EDIT_LOAD_FAILED_MESSAGE); return; }
          /* Locked on the server: the edit ends here, with the reason, rather
             than after the whole wizard and a 403 at the end. */
          if (rec.status && (LOCKED_IN_TRADE.has(rec.status) || LOCKED_CLOSED.has(rec.status))) {
            notify(LOCKED_IN_TRADE.has(rec.status) ? EDIT_IN_TRADE_MESSAGE : EDIT_CLOSED_MESSAGE);
            target.__gbzCancelEdit?.();
            return;
          }
          target.__gbzSeedWizardEdit?.(rec, ctx.seq);
        })
        .catch(() => {
          // A failure for an edit the seller already left is nobody's news.
          const now = target.__gbzEditContext?.();
          if (now && now.seq === ctx.seq) notify(EDIT_LOAD_FAILED_MESSAGE);
        })
        .finally(() => { if (inFlight === ctx.seq) inFlight = null; });
    });
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
